import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

interface Props {
  open: boolean;
  title: string;
  body: React.ReactNode;
  // Inputs:
  inputLabel: string;
  inputPlaceholder?: string;
  value: string;
  onChange: (next: string) => void;
  // Validation feedback. When `errorText` is set, the field renders red
  // and submission is blocked even if the rest of `disabled` is false.
  helperText: string;
  errorText?: string;
  // CTA:
  submitLabel: string;
  submitPendingLabel: string;
  pending: boolean;
  // External "cannot submit" (e.g. value is empty or unchanged). Combines
  // with the local `pending` + `errorText` checks.
  externallyDisabled?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

// Generic "give me a name" dialog. Used for both Create release and Copy
// release — they share the same shape (title + helper paragraph + name
// TextField + Cancel/primary action) and only differ in copy + callback.
export const NameInputDialog: React.FC<Props> = ({
  open,
  title,
  body,
  inputLabel,
  inputPlaceholder,
  value,
  onChange,
  helperText,
  errorText,
  submitLabel,
  submitPendingLabel,
  pending,
  externallyDisabled,
  onClose,
  onSubmit,
}) => {
  const error = !!errorText;
  const submitDisabled = pending || externallyDisabled || error;
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!pending) onClose();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label={inputLabel}
          placeholder={inputPlaceholder}
          fullWidth
          variant="outlined"
          size="small"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !submitDisabled) onSubmit();
          }}
          helperText={errorText ?? helperText}
          error={error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
          variant="contained"
          disabled={submitDisabled}
        >
          {pending ? submitPendingLabel : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
